import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { PageTitle, GBtn, Card, Field, inputCls, StatusBadge, STATUSES } from "./ui";
import { toast } from "sonner";
import { ArrowLeft, Upload, Mail, Printer } from "lucide-react";

export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [o, setO] = useState(null);
  const [status, setStatus] = useState("");
  const fileRef = useRef();

  const load = async () => {
    const { data } = await api.get(`/admin/orders/${id}`);
    setO(data); setStatus(data.status);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const saveStatus = async () => {
    await api.put(`/admin/orders/${id}/status`, { status });
    toast.success("Status updated"); load();
  };
  const resend = async (which) => {
    try { await api.post(`/admin/orders/${id}/resend`, { which }); toast.success("Email sent"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deliver = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/admin/orders/${id}/deliver`, fd); toast.success("Plan uploaded & customer emailed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  if (!o) return <div className="text-white/40">Loading…</div>;

  return (
    <>
      <button onClick={() => nav("/admin/orders")} className="flex items-center gap-2 text-white/50 hover:text-[#D4AF37] text-sm mb-4"><ArrowLeft size={16} /> Back to orders</button>
      <PageTitle title={o.order_ref} subtitle={o.product_name} right={<StatusBadge status={o.status} />} />

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-1">
          <h3 className="font-display text-lg mb-4">Order</h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-white/50">Customer</dt><dd>{o.customer_name || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-white/50">Email</dt><dd className="truncate max-w-[160px]">{o.customer_email || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-white/50">Amount</dt><dd>£{o.amount}</dd></div>
            <div className="flex justify-between"><dt className="text-white/50">Script add-on</dt><dd>{o.add_script ? "Yes" : "No"}</dd></div>
            <div className="flex justify-between"><dt className="text-white/50">Paid</dt><dd>{o.paid_at ? new Date(o.paid_at).toLocaleString("en-GB") : "—"}</dd></div>
          </dl>

          <div className="mt-6 space-y-3">
            <Field label="Change status">
              <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="order-status-select" className={inputCls}>
                {[o.status, ...STATUSES.filter((s) => s !== o.status)].filter((v, i, a) => a.indexOf(v) === i).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <GBtn onClick={saveStatus} data-testid="order-save-status" className="w-full">Save status</GBtn>
          </div>

          <div className="mt-6 pt-6 border-t border-white/10 space-y-2">
            <div className="text-xs uppercase tracking-wider text-white/40 mb-2">Resend email</div>
            <GBtn variant="ghost" className="w-full" onClick={() => resend("order_thankyou")}><Mail size={15} /> Thank-you</GBtn>
            <GBtn variant="ghost" className="w-full" onClick={() => resend("questionnaire_link")}><Mail size={15} /> Questionnaire link</GBtn>
            {o.plan_pdf && <GBtn variant="ghost" className="w-full" onClick={() => resend("delivery")}><Mail size={15} /> Delivery link</GBtn>}
          </div>

          <div className="mt-6 pt-6 border-t border-white/10">
            <div className="text-xs uppercase tracking-wider text-white/40 mb-2">Deliver finished plan (PDF)</div>
            <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => e.target.files[0] && deliver(e.target.files[0])} data-testid="order-deliver-input" />
            <GBtn onClick={() => fileRef.current.click()} data-testid="order-deliver-btn" className="w-full"><Upload size={15} /> {o.plan_pdf ? "Replace plan PDF" : "Upload plan PDF"}</GBtn>
            {o.plan_pdf && <a href={`${API}/admin/orders/${o.id}/plan?token=${o.questionnaire_token}`} target="_blank" rel="noreferrer" className="mt-2 block text-center text-xs text-[#D4AF37] hover:underline">Download current plan</a>}
          </div>
        </Card>

        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg">Questionnaire answers</h3>
            {o.questionnaire && <GBtn variant="ghost" onClick={() => window.print()}><Printer size={15} /> Print</GBtn>}
          </div>
          {o.questionnaire ? (
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
              {Object.entries(o.questionnaire).map(([k, v]) => (
                <div key={k}><dt className="text-white/45 text-xs uppercase tracking-wider">{k.replace(/_/g, " ")}</dt><dd className="mt-0.5 text-white/85 whitespace-pre-wrap">{String(v) || "—"}</dd></div>
              ))}
            </dl>
          ) : (
            <p className="text-white/40 text-sm">Not submitted yet. The customer received a unique link to complete it.</p>
          )}
        </Card>
      </div>
    </>
  );
}
